"use client";

import { useEffect, useState, type CSSProperties, type ReactNode } from "react";

const MIN_WIDTH = 220;
const MAX_WIDTH = 560;

function clamp(px: number): number {
  return Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, Math.round(px)));
}

export interface PanelState {
  width: number;
  /** Desktop: the column is collapsed away. Persisted. */
  hidden: boolean;
  /** Mobile: the overlay drawer is open. Deliberately not persisted. */
  drawer: boolean;
  resizing: boolean;
  setHidden: (v: boolean) => void;
  setDrawer: (v: boolean) => void;
  startResize: (event: React.PointerEvent) => void;
  resetWidth: () => void;
}

/**
 * Width + visibility for one side rail.
 *
 * `hidden` and `drawer` are separate on purpose: the breakpoint is resolved in
 * CSS, not JavaScript, so there is no media-query flash and no wrong-layout
 * first paint. Desktop reads `hidden`, mobile reads `drawer`, and each has the
 * default it should have.
 */
export function usePanelState({
  widthKey,
  hiddenKey,
  defaultWidth,
  side,
}: {
  widthKey: string;
  hiddenKey: string;
  defaultWidth: number;
  side: "left" | "right";
}): PanelState {
  const [width, setWidth] = useState(defaultWidth);
  const [hidden, setHiddenState] = useState(false);
  const [drawer, setDrawer] = useState(false);
  const [resizing, setResizing] = useState(false);

  useEffect(() => {
    const stored = Number(localStorage.getItem(widthKey));
    // localStorage is client-only, so the stored preference has to land after
    // mount — that is what keeps the server markup and hydration in sync.
    // Runs once on mount, so there is no cascade.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setWidth(
      Number.isFinite(stored) && stored > 0 ? clamp(stored) : defaultWidth
    );
    setHiddenState(localStorage.getItem(hiddenKey) === "1");
  }, [widthKey, hiddenKey, defaultWidth]);

  function setHidden(v: boolean) {
    localStorage.setItem(hiddenKey, v ? "1" : "0");
    setHiddenState(v);
  }

  function resetWidth() {
    setWidth(defaultWidth);
    localStorage.setItem(widthKey, String(defaultWidth));
  }

  function startResize(event: React.PointerEvent) {
    event.preventDefault();
    setResizing(true);
    // A right-hand rail grows as the pointer moves left, so measure from the
    // far edge of the window rather than from x = 0.
    const measure = (x: number) =>
      side === "left" ? x : window.innerWidth - x;
    // Persist the last width we actually rendered, not the pointerup
    // coordinate — releasing outside the window reports a different x.
    let next = clamp(measure(event.clientX));
    const onMove = (e: PointerEvent) => {
      next = clamp(measure(e.clientX));
      setWidth(next);
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      setResizing(false);
      localStorage.setItem(widthKey, String(next));
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  }

  return {
    width,
    hidden,
    drawer,
    resizing,
    setHidden,
    setDrawer,
    startResize,
    resetWidth,
  };
}

/**
 * A side rail: a resizable column from `md` up, an overlay drawer below it.
 * Which one applies is decided by CSS alone.
 */
export default function SidePanel({
  side,
  state,
  label,
  children,
}: {
  side: "left" | "right";
  state: PanelState;
  label: string;
  children: ReactNode;
}) {
  const { width, hidden, drawer, resizing, setDrawer, startResize, resetWidth } =
    state;

  return (
    <>
      {drawer && (
        <button
          type="button"
          aria-label={`Close ${label}`}
          onClick={() => setDrawer(false)}
          className="fixed inset-0 z-40 bg-zinc-950/80 backdrop-blur-sm transition-opacity md:hidden"
        />
      )}
      <aside
        aria-label={label}
        style={{ "--panel-w": `${width}px` } as CSSProperties}
        className={[
          "flex-col border-zinc-800 bg-zinc-900 md:bg-zinc-900/50",
          side === "left" ? "border-r" : "border-l",
          // Below md the rail is an overlay drawer, out of the DOM flow.
          drawer
            ? `fixed inset-y-0 z-50 flex shadow-2xl transition-transform duration-200 ease-out ${
                side === "left"
                  ? "left-0 w-[86vw] max-w-sm"
                  : "right-0 w-[92vw] max-w-md sm:max-w-sm"
              }`
            : "hidden",
          // From md up it is a resizable column in the flex row.
          hidden
            ? "md:hidden"
            : "md:relative md:inset-auto md:z-auto md:flex md:w-[var(--panel-w)] md:max-w-none md:shrink-0 md:shadow-none",
          resizing ? "select-none" : "",
        ].join(" ")}
      >
        {children}
        {/* Drag the inner edge to widen or narrow. Pointer only, so it never
            competes with touch scrolling in the drawer. */}
        <div
          onPointerDown={startResize}
          onDoubleClick={resetWidth}
          role="separator"
          aria-orientation="vertical"
          title="Drag to resize · double-click to reset"
          className={`absolute inset-y-0 z-20 hidden w-1.5 cursor-col-resize transition-colors md:block ${
            side === "left" ? "right-0" : "left-0"
          } ${resizing ? "bg-violet-500/60" : "hover:bg-violet-500/40"}`}
        />
      </aside>
    </>
  );
}
